import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import NatureOutlinedIcon from '@mui/icons-material/NatureOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { Box, Container, Divider, Grid, Typography } from '@mui/material';
import React from 'react';
import { COLORS } from '../../Constants/styles';
import { Home_IMG } from '../../Constants/ImageConstants';
import { motion } from 'framer-motion';

interface Feature { icon: React.ElementType; title: string; desc: string; }

interface ContentItem {
  reverse: boolean;
  heading: string;
  subheading: string;
  description: string;
  des2: string;
  features: Feature[] | null;
  image: string;
  imgRotation: { md: string; sm: string; xs: string };
}

const contentData: ContentItem[] = [
  {
    reverse: true,
    heading: 'Transforming the Future Through Sport & Education',
    subheading: "We are building tomorrow's champions on the field and in life.",
    description:
      'Through innovative, curriculum-aligned sports and physical education programs, we empower schools to offer every child an opportunity to move, play, and grow. From KG to Grade 12, from after-school to elite academy training-our ecosystem nurtures fitness, teamwork, leadership, and holistic well-being.',
    des2: "Whether it's a school sports day or a grassroots tournament, a coach's training module or a high performance fitness plan we design, deliver, and elevate sports education that matters.",
    features: null,
    image: Home_IMG.CommunityFunSection,
    imgRotation: { md: 'rotate(-5deg)', sm: 'rotate(3deg)', xs: 'rotate(0deg)' },
  },
  {
    reverse: false,
    heading: 'Why Partner With Us?',
    subheading: "Here's what sets us apart from the rest.",
    description: '',
    des2: '',
    features: [
      { icon: MenuBookOutlinedIcon, title: 'Curriculum-Aligned Programs', desc: 'Designed to seamlessly integrate with school schedules and future-ready learning.' },
      { icon: GroupsOutlinedIcon, title: 'Coach Empowerment', desc: 'Structured coach education to uplift teaching quality and safe play across levels.' },
      { icon: SettingsOutlinedIcon, title: 'End-to-End Sports Solutions', desc: "From infrastructure design to retail supply and event execution we've got you covered." },
      { icon: NatureOutlinedIcon, title: 'Rooted in Grassroots, Built for Growth', desc: "Tailored for South Indian education systems and scalable to every school's need." },
      { icon: InsightsOutlinedIcon, title: 'Measurable Outcomes', desc: 'Tools for tracking physical literacy, fitness gains, and social-emotional development.' },
    ],
    image: Home_IMG.CommunityFunSection2,
    imgRotation: { md: 'rotate(5deg)', sm: 'rotate(-3deg)', xs: 'rotate(0deg)' },
  },
];

const CommunityFunSection = () => {
  return (
    <Box sx={{ background: COLORS.accentDarkGreen, py: { xs: 2, sm: 10, md: 10 } }}>
      <Container maxWidth="xl">
        {contentData.map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: index * 0.2 }}
          >
            <Grid container spacing={6} alignItems="center" sx={{ mt: index === 0 ? 0 : 1 }}>
              <Grid item xs={12} md={7} sx={{ order: { xs: 1, md: item.reverse ? 2 : 1 } }}>
                <Box
                  component="img"
                  src={item.image}
                  alt={item.heading}
                  sx={{ width: '100%', borderRadius: 2, transform: item.imgRotation }}
                />
              </Grid>

              <Grid item xs={12} md={4} sx={{ order: { xs: 2, md: item.reverse ? 1 : 2 } }}>
                <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                  <Typography variant="h2" sx={{ mb: 2, color: COLORS.accentLime }}>
                    {item.heading}
                  </Typography>
                  <Typography sx={{ maxWidth: 500, color: COLORS.white, fontFamily: 'Inter', fontWeight: 700 }}>
                    {item.subheading}
                  </Typography>
                  <Divider sx={{ mt: 1, mb: 2, borderColor: COLORS.white }} />

                  {item.features ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {item.features.map((f) => (
                        <Box key={f.title} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, textAlign: 'left' }}>
                          <f.icon sx={{ fontSize: '1.4rem', color: COLORS.accentLime, flexShrink: 0, mt: '2px' }} />
                          <Box>
                            <Typography sx={{ color: COLORS.white, fontFamily: 'Inter', fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.3 }}>
                              {f.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: COLORS.whiteSemiTransparent, fontFamily: 'Inter', fontSize: '0.8rem' }}>
                              {f.desc}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ maxWidth: 500, color: COLORS.white, fontFamily: 'Inter' }}>
                      {item.description}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </motion.div>
        ))}
      </Container>
    </Box>
  );
};

export default CommunityFunSection;
